import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import getPriorityListItems from 'helpers/getPriorityListItems';

// Components
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import SortableList from './SortableList';
import AttributeSelector from 'components/attributeSelector/Local';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';

// Style
import withStyles from '@material-ui/core/styles/withStyles';
import classnames from 'classnames';

function getSelectedAttributes(selectedAttributes, field, index) {
  return {
    ...selectedAttributes,
    [field.objectKey]: {
      ...field,
      id: field.objectKey,
    },
  };
}

function PriorityList({
  classes,
  configFields,
  editPriorityList,
  isModalOpen,
  onCloseModal,
  priorityList,
  resetState,
}) {
  const [selectedAttributes, setSelectedAttributes] = useState(
    priorityList.reduce(getSelectedAttributes, {}),
  );

  useEffect(() => {
    setSelectedAttributes(priorityList.reduce(getSelectedAttributes, {}));
  }, [priorityList]);

  const getAttributeKey = attribute => attribute.id;

  const getAttributeDisplayName = attribute => attribute.displayName;

  const changeOptOutDefault = key => {
    const optOutNo = {
      skills: true,
      bio: true,
      interests: true,
    };

    return optOutNo[key];
  };

  const onSaveSelectedAttributes = (checkedAttributes = {}) => {
    const newFields = Object.keys(checkedAttributes).map((key, index) => {
      // if already selected, take that value, if not, then run this.changeOptOutDefault and get default optout value
      const canOptOut = selectedAttributes[key]
        ? selectedAttributes[key].canOptOut
        : !changeOptOutDefault(key);

      return {
        objectKey: key,
        canOptOut: canOptOut,
      };
    });

    const newSelectedAttributes = Object.keys(selectedAttributes).map((key, index) => {
      return {
        objectKey: key,
        ...selectedAttributes[key],
      };
    });

    editPriorityList([...newFields, ...newSelectedAttributes]);
    setSelectedAttributes(newFields.reduce(getSelectedAttributes, {}));
  };

  const handleCancel = () => {
    resetState();
    onCloseModal();
  };

  const configFieldCollectionFiltered = useMemo(
    () => getPriorityListItems(configFields, priorityList),
    [configFields, priorityList],
  );

  const attributesSelectorElem = (
    <AttributeSelector
      label="Add Attribute"
      title="Select Attributes..."
      attributes={configFieldCollectionFiltered}
      selectedAttributes={{}}
      searchPlaceholder="Search for an attribute..."
      getAttributeKey={getAttributeKey}
      getAttributeDisplayName={getAttributeDisplayName}
      onSaveSelectedAttributes={onSaveSelectedAttributes}
      classes={{ attributes: classes.attributesList }}
      LabelButtonProps={{
        color: 'secondary',
      }}
      saveButtonLabel="Add"
    />
  );

  const headerTitleElem = (
    <div className={classes.headerTitle}>
      Edit Attribute Priority
      {attributesSelectorElem}
    </div>
  );

  const attributeHeaderCellClasses = classnames(classes.TableHeadCellRoot, classes.attributeHeader);

  return (
    <Dialog
      classes={{ paper: classes.rootDialog }}
      onClose={onCloseModal}
      open={isModalOpen}
      data-testid="priority-modal"
    >
      <DialogTitle>{headerTitleElem}</DialogTitle>
      <DialogContent className={classes.content}>
        {priorityList.length ? (
          <Table>
            <TableHead>
              <TableHeadCell classes={{ root: attributeHeaderCellClasses }}>
                Attribute
              </TableHeadCell>
              <TableHeadCell classes={{ root: classes.TableHeadCellRoot }}>
                Can user opt out?
                <Tooltip
                  title="If user opts out of the field it will count towards profile completion"
                  placement="top"
                >
                  <img
                    src="https://siftprodcoredata.z13.web.core.windows.net/siftgeneral/fill-1%403x.png"
                    alt="question-mark"
                    className={classes.questionMark}
                  />
                </Tooltip>
              </TableHeadCell>
            </TableHead>
          </Table>
        ) : (
          'No items to display. Add an attribute to create your Priority List.'
        )}
        {priorityList && (
          <SortableList
            list={priorityList}
            editPriorityList={editPriorityList}
            configFields={configFields}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button data-testid="footer cancel" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="secondary"
          disableElevation={true}
          onClick={onCloseModal}
          variant="contained"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PriorityList.propTypes = {
  classes: PropTypes.object.isRequired,
  configFields: PropTypes.object.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  priorityList: PropTypes.array.isRequired,
  // Actions
  editPriorityList: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
};

const styles = theme => {
  return {
    root: {
      overflow: 'visible',
    },
    rootDialog: {
      minHeight: 580,
      minWidth: 600,
    },
    attributeHeader: {
      width: '46%',
      paddingLeft: theme.spacing(0.5),
    },
    attributesList: {
      fontSize: 16,
    },
    TableHeadCellRoot: {
      borderBottom: 'none',
    },
    headerTitle: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    questionMark: {
      width: 15,
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(-0.25),
    },
  };
};
export default withStyles(styles)(PriorityList);
